export type ApiSuccess<T> = {
  success: true;
  message?: string;
  data?: T;
  [key: string]: unknown;
};

export type ApiFailure = {
  success: false;
  error: string;
  code?: string;
  request_id?: string;
  [key: string]: unknown;
};

export type ApiResult<T> = ApiSuccess<T> | ApiFailure;

export function createRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`;
}

export function apiBaseUrl(override?: string): string {
  if (override && override.trim() !== '') {
    return override.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
      return '/api';
    }
    // Dashboard serves /api via web/api bridge; native/other hosts use the API domain.
    if (host === 'dashboard.eshhanly.com') {
      return '/api';
    }
    if (host === 'eshhanly.com' || host === 'www.eshhanly.com' || host === 'api.eshhanly.com') {
      return 'https://api.eshhanly.com';
    }
  }
  return '/api';
}

export type ApiClientOptions = {
  appClient: 'customer' | 'admin' | 'web';
  baseUrl?: string;
  getToken?: () => string | null;
};

export class ApiClient {
  constructor(private readonly options: ApiClientOptions) {}

  async request<T>(
    method: 'GET' | 'POST',
    path: string,
    body?: Record<string, unknown>,
    extraHeaders: Record<string, string> = {},
  ): Promise<ApiResult<T>> {
    const requestId = createRequestId();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-App-Client': this.options.appClient,
      'X-Request-Id': requestId,
      ...extraHeaders,
    };
    const token = this.options.getToken?.();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const base = apiBaseUrl(this.options.baseUrl);
    const response = await fetch(`${base}${path.startsWith('/') ? path : `/${path}`}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    });

    const payload = (await response.json().catch(() => ({
      success: false,
      error: 'استجابة غير صالحة من الخادم.',
    }))) as ApiResult<T>;

    if (!('success' in payload)) {
      return { success: false, error: 'صيغة رد غير متوقعة.', request_id: requestId };
    }
    if (!payload.success && !payload.request_id) {
      payload.request_id = requestId;
    }
    return payload;
  }

  get<T>(path: string): Promise<ApiResult<T>> {
    return this.request<T>('GET', path);
  }

  post<T>(path: string, body: Record<string, unknown> = {}): Promise<ApiResult<T>> {
    return this.request<T>('POST', path, body);
  }
}
